import type { ForqSupabaseClient } from './client';
import type {
  AnnouncementRepeat,
  AnnouncementTone,
  AuditRecord,
  ClaimedNumber,
  SystemAnnouncement,
  TenantNumberPrefix,
  TenantSummary,
} from '@forq/contracts';
import { isTenantNumberPrefix } from '@forq/contracts';
import type { ClaimNumberInput, PlatformDataGateway } from '@forq/platform-data';
import {
  SessionUserMismatchError,
  SupabaseOperationError,
} from './errors';

interface TenantRow {
  readonly id: string;
  readonly name: string;
  readonly short_name: string;
  readonly number_prefix: string;
}

interface PermissionRow {
  readonly permission_key: string;
}

interface NumberAllocationRow {
  readonly allocation_id: string;
  readonly sequence_id: string;
  readonly allocated_number: string;
  readonly counter_value: number;
  readonly claimed_at: string;
}

interface AuditRow {
  readonly id: string;
  readonly tenant_id: string | null;
  readonly actor_user_id: string | null;
  readonly action: string;
  readonly entity_type: string;
  readonly entity_id: string | null;
  readonly old_data: Readonly<Record<string, unknown>> | null;
  readonly new_data: Readonly<Record<string, unknown>> | null;
  readonly metadata: Readonly<Record<string, unknown>> | null;
  readonly occurred_at: string;
}

export interface AnnouncementRow {
  readonly id: string;
  readonly title: string;
  readonly body: string;
  readonly tone: string;
  readonly repeat_mode: string;
  readonly updated_at: string;
}

const announcementTones: readonly string[] = ['info', 'warning', 'critical'];
const announcementRepeats: readonly string[] = ['every-start', 'once'];

/**
 * Die Datenbank hat CHECK-Constraints auf beiden Feldern -- unbekannte Werte kann es also nur
 * geben, wenn jemand die Tabelle erweitert und diesen Adapter vergisst. Dann faellt die Meldung
 * bewusst auf die harmlose Variante zurueck, statt undefined ins Fenster zu schreiben.
 */
function toTone(value: string): AnnouncementTone {
  return announcementTones.includes(value) ? (value as AnnouncementTone) : 'info';
}

function toRepeat(value: string): AnnouncementRepeat {
  return announcementRepeats.includes(value) ? (value as AnnouncementRepeat) : 'every-start';
}

export function mapAnnouncement(row: AnnouncementRow): SystemAnnouncement {
  return {
    id: row.id,
    title: row.title,
    body: row.body,
    tone: toTone(row.tone),
    repeat: toRepeat(row.repeat_mode),
    updatedAt: row.updated_at,
  };
}

/**
 * Das Nummernpräfix aus der Datenbank prüfen.
 *
 * Vorher waren hier nur '1','2','3' zugelassen — die drei Firmen, für die FORQ
 * gebaut wurde. Ein vierter Mandant brachte damit den Start zum Absturz, obwohl
 * die Datenbank 1–9 zulässt. Geprüft wird jetzt gegen dieselbe Regel wie dort.
 *
 * Die Prüfung bleibt: die Datenbank kann durch eine spätere Änderung etwas
 * liefern, das der Typ nicht kennt, und ein stilles Durchreichen wäre schlimmer
 * als eine klare Meldung.
 */
function assertNumberPrefix(value: string): TenantNumberPrefix {
  if (isTenantNumberPrefix(value)) return value;
  throw new SupabaseOperationError(
    'platform.getTenants',
    `Das Nummernpräfix "${value}" ist keine Ziffer 1–9. ` +
    'Erlaubt ist genau eine Ziffer; die Datenbank prüft dasselbe.',
  );
}

function messageOf(error: { readonly message: string } | null): string {
  return error?.message ?? 'Unknown Supabase error.';
}

export class SupabasePlatformDataGateway implements PlatformDataGateway {
  constructor(private readonly client: ForqSupabaseClient) {}

  private async assertAuthenticatedUser(userId: string): Promise<void> {
    const { data, error } = await this.client.auth.getUser();
    if (error) throw new SupabaseOperationError('auth.getUser', error.message);
    if (!data.user || data.user.id !== userId) throw new SessionUserMismatchError();
  }

  async listTenantsForUser(userId: string): Promise<readonly TenantSummary[]> {
    await this.assertAuthenticatedUser(userId);
    const { data, error } = await this.client.rpc('get_my_tenants');
    if (error) throw new SupabaseOperationError('platform.getTenants', messageOf(error));

    return ((data ?? []) as TenantRow[]).map((row) => ({
      id: row.id,
      name: row.name,
      shortName: row.short_name,
      numberPrefix: assertNumberPrefix(row.number_prefix),
    }));
  }

  async resolvePermissions(userId: string, tenantId: string): Promise<ReadonlySet<string>> {
    await this.assertAuthenticatedUser(userId);
    const { data, error } = await this.client.rpc('list_my_permissions', {
      p_tenant_id: tenantId,
    });
    if (error) throw new SupabaseOperationError('platform.getPermissions', messageOf(error));
    return new Set(((data ?? []) as PermissionRow[]).map((row) => row.permission_key));
  }

  async claimNumber(input: ClaimNumberInput): Promise<ClaimedNumber> {
    await this.assertAuthenticatedUser(input.userId);
    const { data, error } = await this.client.rpc('claim_number_v2', {
      p_tenant_id: input.tenantId,
      p_sequence_key: input.key,
      p_sequence_year: input.year ?? null,
      p_source_entity_type: input.sourceEntityType ?? null,
      p_source_entity_id: input.sourceEntityId ?? null,
    });
    if (error) throw new SupabaseOperationError('platform.claimNumber', messageOf(error));

    const row = ((data ?? []) as NumberAllocationRow[])[0];
    if (!row) throw new SupabaseOperationError('platform.claimNumber', 'No allocation was returned.');

    return {
      sequenceId: row.sequence_id,
      number: row.allocated_number,
      counterValue: row.counter_value,
      claimedAt: row.claimed_at,
    };
  }

  async listAuditRecords(tenantId: string): Promise<readonly AuditRecord[]> {
    const { data, error } = await this.client.rpc('list_my_audit_records', {
      p_tenant_id: tenantId,
      p_limit: 200,
    });
    if (error) throw new SupabaseOperationError('platform.getAudit', messageOf(error));

    return ((data ?? []) as AuditRow[]).map((row) => ({
      id: row.id,
      tenantId: row.tenant_id,
      actorUserId: row.actor_user_id ?? 'system',
      action: row.action,
      entityType: row.entity_type,
      entityId: row.entity_id ?? undefined,
      before: row.old_data ?? undefined,
      after: row.new_data ?? undefined,
      metadata: row.metadata ?? undefined,
      occurredAt: row.occurred_at,
    }));
  }

  async loadActiveAnnouncement(tenantId: string): Promise<SystemAnnouncement | null> {
    const { data, error } = await this.client.rpc('get_active_announcement', {
      p_tenant_id: tenantId,
    });
    if (error) throw new SupabaseOperationError('platform.getAnnouncement', messageOf(error));
    return data ? mapAnnouncement(data as unknown as AnnouncementRow) : null;
  }
}
